import { FileExtensions } from '~/shared/api'
import {
  DOCUMENT_MIME_TYPES,
  GB,
  IMAGE_MIME_TYPES,
  KB,
  MB,
  MediaType,
  MIME_TO_EXTENSION,
  VIDEO_MIME_TYPES,
} from '../../const/files'
import { delay } from '../delay'
import { useNotificationStore } from '../use/notification'
import { detectFileTypeFromBlob } from './detectFileType'

export { detectFileTypeFromBlob } from './detectFileType'
export { exportElementToPdf } from './exportElementToPdf'
export type { ExportElementToPdfOptions, ExportElementToPdfPageFormat } from './exportElementToPdf'

export function formatFileSize(bytes: number): string {
  if (bytes < KB) {
    return `${bytes} Б`
  }
  if (bytes < MB) {
    return `${Math.round(bytes / KB)} КБ`
  }
  if (bytes < GB) {
    return `${Math.round(bytes / MB)} МБ`
  }
  return `${(bytes / GB).toFixed(1)} ГБ`
}

function isMimeInList(mime: string, list: readonly string[]): boolean {
  return list.includes(mime.toLowerCase())
}

/**
 * Определяет тип медиа по содержимому файла (file-type + SVG fallback).
 */
export async function detectMediaTypeFromFile(file: File): Promise<MediaType | null> {
  const result = await detectFileTypeFromBlob(file)

  if (!result) {
    return null
  }

  if (isMimeInList(result.mime, IMAGE_MIME_TYPES)) {
    return MediaType.Image
  }

  if (isMimeInList(result.mime, VIDEO_MIME_TYPES)) {
    return MediaType.Video
  }

  return null
}

/**
 * Проверяет, соответствует ли файл ожидаемому типу медиа.
 * Использует file-type для надёжной проверки (Android может не передавать MIME).
 */
export async function isValidMediaFile(file: File, mediaType: MediaType): Promise<boolean> {
  const declaredMime = file.type?.toLowerCase()

  if (mediaType === MediaType.Image) {
    if (declaredMime && isMimeInList(declaredMime, IMAGE_MIME_TYPES)) {
      return true
    }

    const result = await detectFileTypeFromBlob(file)
    return result !== undefined && isMimeInList(result.mime, IMAGE_MIME_TYPES)
  }

  if (mediaType === MediaType.Video) {
    if (declaredMime && isMimeInList(declaredMime, VIDEO_MIME_TYPES)) {
      return true
    }

    const result = await detectFileTypeFromBlob(file)
    return result !== undefined && isMimeInList(result.mime, VIDEO_MIME_TYPES)
  }

  return false
}

/**
 * Фильтрует массив файлов, оставляя только те, что соответствуют типу медиа.
 * Удаляет файлы с неверным MIME (постобработка для Android).
 * @param files - массив файлов для проверки
 * @param mediaType - если undefined, принимаются и изображения, и видео
 */
export async function filterValidMediaFiles(
  files: File[],
  mediaType?: MediaType,
): Promise<File[]> {
  if (!mediaType) {
    const results = await Promise.all(
      files.map(async (file) => {
        const detected = await detectMediaTypeFromFile(file)
        return { file, valid: detected !== null }
      }),
    )
    return results.filter(r => r.valid).map(r => r.file)
  }

  const results = await Promise.all(
    files.map(async file => ({ file, valid: await isValidMediaFile(file, mediaType) })),
  )
  return results.filter(r => r.valid).map(r => r.file)
}

/**
 * Фильтрует массив файлов, оставляя только документы (PDF, DOC, DOCX и т.д.).
 */
export async function filterValidDocumentFiles(files: File[]): Promise<File[]> {
  return files.filter((file) => {
    const declaredMime = file.type?.toLowerCase()
    return declaredMime && isMimeInList(declaredMime, DOCUMENT_MIME_TYPES as unknown as string[])
  })
}

export function getDataUrlFromFirstFile(files: File[]) {
  if (files.length > 0) {
    const file = files[0]
    if (file && file.type.startsWith('image/')) {
      return URL.createObjectURL(file)
    }
  }

  return ''
}

export function getFileFromDataURL(dataurl: string, filename: string) {
  const arr = dataurl.split(',')
  const mime = arr[0].match(/:(.*?);/)?.[1]
  const bstr = atob(arr[arr.length - 1])
  let n = bstr.length
  const u8arr = new Uint8Array(n)
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n)
  }
  return new File([u8arr], filename, { type: mime })
}

export function isDataURL(dataurl: string) {
  return dataurl.startsWith('data:')
}

export function getMimeTypeFromDataURL(dataURL: string): string | null {
  const mimeMatch = dataURL.match(/^data:(.*?);/)
  return mimeMatch ? mimeMatch[1] : null
}

export function getFileExtensionFromMimeType(mimeType: string): string {
  return MIME_TO_EXTENSION[mimeType as keyof typeof MIME_TO_EXTENSION] ?? ''
}

export function getFileExtensionFromDataURL(
  dataURL: string,
): FileExtensions | null {
  const mimeType = getMimeTypeFromDataURL(dataURL)
  if (!mimeType) {
    return null
  }

  const mimeToExt: Record<string, FileExtensions> = {
    'image/jpeg': FileExtensions.Jpeg,
    'image/jpg': FileExtensions.Jpg,
    'image/png': FileExtensions.Png,
    'image/webp': FileExtensions.Webp,
  }

  return mimeToExt[mimeType] || null
}

export function getExtensionFromDataURL(
  dataURL: string,
): string {
  const mimeType = getMimeTypeFromDataURL(dataURL)
  if (!mimeType) {
    return ''
  }

  return getFileExtensionFromMimeType(mimeType)
}

export async function uploadFileToPresignedUrl(
  file: File,
  url: string,
): Promise<Response | void> {
  try {
    const response = await fetch(url, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    })

    if (!response.ok) {
      throw new Error(`Ошибка загрузки: ${response.statusText}`)
    }

    await delay(2000)
  }
  catch {
    useNotificationStore().notify({
      message: 'Ошибка при загрузке файла',
      type: 'error',
    })
  }
}

export async function downloadFile(url: string, filename: string) {
  try {
    const response = await fetch(url, { cache: 'no-store' })

    if (!response.ok) {
      throw new Error(`Ошибка загрузки: ${response.statusText}`)
    }

    const blob = await response.blob()
    const blobUrl = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = blobUrl
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    URL.revokeObjectURL(blobUrl)
  }
  catch {
    useNotificationStore().notify({
      message: 'Ошибка при скачивании файла',
      type: 'error',
    })
  }
}

export function getLocalMediaUrl(file: File): string {
  return URL.createObjectURL(file)
}

export async function getBlobFromUrl(url: string): Promise<Blob> {
  const response = await fetch(url, { cache: 'no-store' })

  if (!response.ok) {
    throw new Error(`Ошибка загрузки blob из URL: ${response.statusText}`)
  }

  return await response.blob()
}

export function isBlobUrl(url: string) {
  return url.startsWith('blob:')
}

export function isHlsVideoUrl(url: string) {
  return url.includes('m3u8')
}

function isVideoUrlPath(url: string): boolean {
  const normalized = url.toLowerCase().split('?')[0]?.split('#')[0] ?? ''

  return VIDEO_MIME_TYPES.some((mime) => {
    const extension = MIME_TO_EXTENSION[mime as keyof typeof MIME_TO_EXTENSION]
    return extension ? normalized.endsWith(`.${extension}`) : false
  })
}

export function detectMediaTypeFromUrl(url: string): MediaType {
  if (isDataURL(url)) {
    const mime = getMimeTypeFromDataURL(url)

    if (mime && isMimeInList(mime, VIDEO_MIME_TYPES)) {
      return MediaType.Video
    }

    return MediaType.Image
  }

  if (isHlsVideoUrl(url) || isVideoUrlPath(url)) {
    return MediaType.Video
  }

  return MediaType.Image
}
